import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Lock, LogOut, Send, Trash2, Slack, Plus, Sunrise } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  adminLogin, getAdminSession, setAdminSession, listSlack, addSlack, toggleSlack, deleteSlack, testSlack, formatApiError,
  sendMorningBrief, lastMorningBrief,
} from "@/lib/api";

const inputCls = "bg-slate-900/70 border border-border rounded-md px-3 py-2 text-sm font-mono-t focus:outline-none focus:border-cyan";
const btnCls = "bg-cyan text-slate-950 font-heading font-semibold uppercase tracking-wide text-sm py-2 rounded-md hover:bg-cyan/85 transition-colors disabled:opacity-50";

function AdminLoginForm({ onSession }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const s = await adminLogin(email, password);
      setAdminSession(s); onSession(s);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <input data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" className={inputCls} />
      <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} />
      {err && <div data-testid="admin-login-error" className="font-mono-t text-[11px] text-risk-critical">{err}</div>}
      <button data-testid="admin-login-submit" disabled={busy || !email || !password} className={btnCls}>
        {busy ? "Verifying…" : "Sign in as admin"}
      </button>
    </form>
  );
}

function HookRow({ h, onChanged }) {
  const run = async (fn, ok) => {
    try { const r = await fn(); if (ok) ok(r); onChanged(); } catch (ex) { toast.error(formatApiError(ex)); }
  };
  const ls = h.last_status;
  return (
    <div data-testid={`slack-hook-${h.id}`} className="border border-border rounded-md p-2 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Slack className="w-3.5 h-3.5 text-cyan shrink-0" />
        <span className="font-heading text-sm text-slate-100 truncate flex-1">{h.label}</span>
        <Switch data-testid={`slack-toggle-${h.id}`} checked={h.enabled} onCheckedChange={(v) => run(() => toggleSlack(h.id, v))} />
        <button data-testid={`slack-test-${h.id}`} title="Send test message" className="text-slate-400 hover:text-cyan"
          onClick={() => run(() => testSlack(h.id), (r) => (r.ok ? toast.success("Test message delivered") : toast.error(`Slack rejected it (${r.status ?? "no response"})`)))}>
          <Send className="w-3.5 h-3.5" />
        </button>
        <button data-testid={`slack-delete-${h.id}`} title="Remove" className="text-slate-400 hover:text-risk-critical"
          onClick={() => run(() => deleteSlack(h.id), () => toast.success("Destination removed"))}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="font-mono-t text-[9px] text-slate-500 truncate">{h.url_masked}</div>
      {ls && (
        <div className={`font-mono-t text-[9px] ${ls.ok ? "text-emerald-400" : "text-risk-critical"}`}>
          Last send {ls.ok ? "delivered" : `failed (${ls.status ?? "no response"})`} · {new Date(h.last_sent_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function AddHookForm({ onAdded }) {
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await addSlack(label, url); setLabel(""); setUrl(""); toast.success("Slack channel added"); onAdded(); }
    catch (ex) { toast.error(formatApiError(ex)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-2 border-t border-border pt-3">
      <input data-testid="slack-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Name, e.g. #dem-ops" className={inputCls} />
      <input data-testid="slack-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://hooks.slack.com/services/…" className={inputCls} />
      <button data-testid="slack-add-button" disabled={busy || !label.trim() || !url.trim()} className={`${btnCls} flex items-center justify-center gap-1.5`}>
        <Plus className="w-4 h-4" /> {busy ? "Adding…" : "Add Slack channel"}
      </button>
    </form>
  );
}

function MorningBrief() {
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { lastMorningBrief().then(setLast).catch(() => {}); }, []);
  const send = async () => {
    setBusy(true);
    try {
      const r = await sendMorningBrief();
      if (r.destinations === 0) toast.error("No Slack channel switched on");
      else toast.success(`Morning brief posted to ${r.sent}/${r.destinations} channel(s)`);
      lastMorningBrief().then(setLast).catch(() => {});
    } catch (ex) { toast.error(formatApiError(ex)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="morning-brief-row" className="border-t border-border pt-3 flex items-center gap-2">
      <Sunrise className="w-4 h-4 text-cyan shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-heading text-sm text-slate-100">Morning brief · daily 07:00 Barbados time</div>
        <div data-testid="morning-brief-last" className="font-mono-t text-[9px] text-slate-400 truncate">
          {last?.at ? `Last posted ${new Date(last.at).toLocaleString("en-GB", { timeZone: "America/Barbados", dateStyle: "medium", timeStyle: "short" })} AST` : "Not posted yet"}
        </div>
      </div>
      <button data-testid="morning-brief-send" onClick={send} disabled={busy}
        className="border border-cyan/50 text-cyan rounded-md px-2.5 py-1.5 font-mono-t text-[10px] uppercase hover:bg-cyan/10 transition-colors disabled:opacity-50">
        {busy ? "Posting…" : "Send now"}
      </button>
    </div>
  );
}

function RoutingManager({ session, onSignOut, onChange }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    listSlack().then(setData).catch((ex) => {
      if (ex?.response?.status === 401) onSignOut();
      toast.error(formatApiError(ex));
    });
  }, [onSignOut]);
  useEffect(load, [load]);
  const changed = () => { load(); onChange && onChange(); };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between font-mono-t text-[10px] text-slate-400">
        <span data-testid="admin-session-email">{session.email}</span>
        <button data-testid="admin-signout-button" onClick={onSignOut} className="flex items-center gap-1 hover:text-risk-critical">
          <LogOut className="w-3 h-3" /> Sign out
        </button>
      </div>
      {data && <div className="font-mono-t text-[10px] text-slate-500">{data.rule}</div>}
      <div data-testid="slack-hook-list" className="flex flex-col gap-2 max-h-[260px] overflow-y-auto">
        {data && data.destinations.length === 0 && (
          <div className="font-mono-t text-xs text-slate-600 text-center py-3">No Slack channels yet</div>
        )}
        {data?.destinations.map((h) => <HookRow key={h.id} h={h} onChanged={changed} />)}
      </div>
      <AddHookForm onAdded={changed} />
      <MorningBrief />
    </div>
  );
}

export function AlertRoutingDialog({ open, onOpenChange, onChange }) {
  const [session, setSession] = useState(getAdminSession());
  const signOut = useCallback(() => { setAdminSession(null); setSession(null); }, []);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="alert-routing-dialog" className="bg-[#06163d] border-border text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
            <Lock className="w-4 h-4 text-cyan" /> Alert Routing
          </DialogTitle>
          <DialogDescription className="font-mono-t text-[11px] text-slate-400">
            Red and amber alerts are posted to these Slack channels. Admin only.
          </DialogDescription>
        </DialogHeader>
        {session ? <RoutingManager session={session} onSignOut={signOut} onChange={onChange} /> : <AdminLoginForm onSession={setSession} />}
      </DialogContent>
    </Dialog>
  );
}
